import { useState, useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Kontakt() {
  useEffect(() => {
    // Update page title and meta description
    document.title = 'Kontakt | CzechAI - Konzultace AI řešení pro firmy';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Kontaktujte CzechAI pro konzultaci AI řešení. Praha +420 704 105 520, Brno +420 775 993 829. Ozveme se do 24 hodin. Nezávazná konzultace zdarma.');
    }

    // Add Schema.org JSON-LD for FAQ
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Jak dlouho trvá implementace AI řešení?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Doba implementace závisí na složitosti projektu. Jednoduché chatboty dokážeme spustit během 2–3 týdnů, komplexní systémy trvají obvykle 2–6 měsíců. Vždy začínáme pilotním projektem, který umožňuje vše otestovat v praxi."
          }
        },
        {
          "@type": "Question", 
          "name": "Kolik stojí AI implementace?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Cena se odvíjí od rozsahu a technické náročnosti řešení. Základní automatizace začíná na desítkách tisíc korun, komplexní systémy se pohybují ve stovkách tisíc. Nabízíme různé cenové modely včetně měsíčních plateb podle možností klienta."
          }
        },
        {
          "@type": "Question",
          "name": "Potřebujeme vlastní IT tým?", 
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ne, naše řešení jsou navržena tak, aby je zvládli běžní uživatelé bez speciálních znalostí. Zajišťujeme kompletní zaškolení a dlouhodobou technickou podporu."
          }
        },
        {
          "@type": "Question",
          "name": "Jak zajišťujete bezpečnost dat?",
          "acceptedAnswer": {
            "@type": "Answer", 
            "text": "Používáme nejmodernější šifrování a pravidelné bezpečnostní audity. Splňujeme požadavky GDPR a dbáme na ochranu citlivých informací ve všech fázích projektu."
          }
        }
      ]
    });
    document.head.appendChild(script);

    // Add ContactPage Schema
    const contactScript = document.createElement('script');
    contactScript.type = 'application/ld+json';
    contactScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Kontakt - CzechAI",
      "description": "Kontaktní informace a formulář pro CzechAI",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/kontakt`,
      "mainEntity": {
        "@type": "Organization",
        "name": "CzechAI",
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "telephone": "+420-732-941-900",
            "contactType": "customer service",
            "areaServed": "CZ",
            "availableLanguage": "Czech"
          },
          {
            "@type": "ContactPoint",
            "telephone": "+420-704-105-520", 
            "contactType": "customer service",
            "areaServed": "Praha",
            "availableLanguage": "Czech"
          },
          {
            "@type": "ContactPoint",
            "telephone": "+420-775-993-829",
            "contactType": "customer service", 
            "areaServed": "Brno",
            "availableLanguage": "Czech"
          }
        ],
        "email": "info@czechai.io"
      }
    });
    document.head.appendChild(contactScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(contactScript);
    };
  }, []);

  const [formData, setFormData] = useState({
    jmeno: '',
    email: '',
    telefon: '',
    firma: '',
    zprava: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Prepare form data for submission
    const formDataToSend = new FormData();
    formDataToSend.append('jmeno', formData.jmeno);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('telefon', formData.telefon);
    formDataToSend.append('firma', formData.firma);
    formDataToSend.append('zprava', formData.zprava);

    try {
      // Submit form to info@czechai.io
      await fetch('mailto:info@czechai.io', {
        method: 'POST',
        body: formDataToSend
      });
      
      setSubmitMessage('Děkujeme za váš zájem! Ozveme se vám do 24 hodin.');
      setFormData({
        jmeno: '',
        email: '',
        telefon: '',
        firma: '',
        zprava: ''
      });
    } catch (error) {
      setSubmitMessage('Zpráva byla připravena k odeslání na info@czechai.io');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero sekce */}
      <section className="relative">
        <div className="w-full h-auto">
          <img 
            src="https://static.readdy.ai/image/2e0b1e22c1b74afa84b035f494e9bd64/bb8cc53ac3a0aed1c0c6da59b9e54286.jfif"
            alt="Kontaktujte nás"
            className="w-full h-auto object-cover"
          />
        </div>
        
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Kontaktujte nás</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">Rádi vám poradíme s implementací AI řešení pro vaši firmu. Ozveme se vám do 24 hodin.</p>
          </div>
        </div>
      </section>

      {/* Kontaktní informace a formulář */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Kontaktní informace */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
                Kontaktujte nás
              </h2>
              <p className="text-lg text-gray-700 mb-8">
                Rádi si s vámi promluvíme o vašich potřebách a možnostech využití AI ve vaší firmě.
              </p>
              
              <div className="space-y-6">
                {/* Vedení */}
                <div>
                  <h3 className="text-lg font-semibold text-black mb-3">Vedení</h3>
                  <div className="space-y-3 ml-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3 border border-gray-200">
                        <i className="ri-phone-line text-black text-lg"></i>
                      </div>
                      <div>
                        <div className="font-medium text-black">+420 732 941 900</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3 border border-gray-200">
                        <i className="ri-mail-line text-black text-lg"></i>
                      </div>
                      <div>
                        <div className="text-gray-600">info@czechai.io</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Praha */}
                <div>
                  <h3 className="text-lg font-semibold text-black mb-3">Praha</h3>
                  <div className="space-y-3 ml-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3 border border-gray-200">
                        <i className="ri-phone-line text-black text-lg"></i>
                      </div>
                      <div>
                        <div className="font-medium text-black">+420 704 105 520</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3 border border-gray-200">
                        <i className="ri-mail-line text-black text-lg"></i>
                      </div>
                      <div>
                        <div className="text-gray-600">praha@czechai.io</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Brno */}
                <div>
                  <h3 className="text-lg font-semibold text-black mb-3">Brno</h3>
                  <div className="space-y-3 ml-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3 border border-gray-200">
                        <i className="ri-phone-line text-black text-lg"></i>
                      </div>
                      <div>
                        <div className="font-medium text-black">+420 775 993 829</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3 border border-gray-200">
                        <i className="ri-mail-line text-black text-lg"></i>
                      </div>
                      <div>
                        <div className="text-gray-600">brno@czechai.io</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4 border border-gray-200">
                    <i className="ri-time-line text-black text-xl"></i>
                  </div>
                  <div>
                    <div className="font-medium text-black">Online dostupnost</div>
                    <div className="text-gray-600">
                      Po-Pá: 8:00 - 17:00<br />
                      So-Ne: Na domluvě
                    </div>
                  </div>
                </div>
              </div>

              {/* Sociální sítě */}
              <div className="mt-12">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Sledujte nás</h3>
                <div className="flex space-x-4">
                  <a href="#" className="w-10 h-10 bg-black rounded-lg flex items-center justify-center text-white hover:bg-gray-800 transition-colors cursor-pointer">
                    <i className="ri-linkedin-fill"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-black rounded-lg flex items-center justify-center text-white hover:bg-gray-800 transition-colors cursor-pointer">
                    <i className="ri-twitter-x-fill"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-black rounded-lg flex items-center justify-center text-white hover:bg-gray-800 transition-colors cursor-pointer">
                    <i className="ri-facebook-fill"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-black rounded-lg flex items-center justify-center text-white hover:bg-gray-800 transition-colors cursor-pointer">
                    <i className="ri-instagram-fill"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-black rounded-lg flex items-center justify-center text-white hover:bg-gray-800 transition-colors cursor-pointer">
                    <i className="ri-tiktok-fill"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-black rounded-lg flex items-center justify-center text-white hover:bg-gray-800 transition-colors cursor-pointer">
                    <i className="ri-youtube-fill"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Kontaktní formulář */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Napište nám
              </h2>

              {submitMessage && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800">{submitMessage}</p>
                </div>
              )}

              <form 
                id="contact-form" 
                onSubmit={handleSubmit} 
                className="space-y-6"
                action="mailto:info@czechai.io"
                method="post"
                encType="application/x-www-form-urlencoded"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="jmeno" className="block text-sm font-medium text-gray-700 mb-2">
                      Jméno a příjmení *
                    </label>
                    <input
                      type="text"
                      id="jmeno"
                      name="jmeno"
                      required
                      value={formData.jmeno}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm"
                      placeholder="Vaše jméno"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm"
                      placeholder="vas@email.cz"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="telefon" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      id="telefon"
                      name="telefon"
                      value={formData.telefon}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm"
                      placeholder="+420 123 456 789"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="firma" className="block text-sm font-medium text-gray-700 mb-2">
                      Firma
                    </label>
                    <input
                      type="text"
                      id="firma"
                      name="firma"
                      value={formData.firma}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm"
                      placeholder="Název vaší firmy"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="zprava" className="block text-sm font-medium text-gray-700 mb-2">
                    Zpráva *
                  </label>
                  <textarea
                    id="zprava"
                    name="zprava"
                    rows={6}
                    required
                    value={formData.zprava}
                    onChange={handleChange}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm resize-none"
                    placeholder="Popište nám vaši situaci a jak vám můžeme pomoci..."
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {formData.zprava.length}/500 znaků
                  </p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="souhlas"
                    required
                    className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
                  />
                  <label htmlFor="souhlas" className="ml-2 text-sm text-gray-600">
                    Souhlasím se zpracováním osobních údajů pro účely této poptávky *
                  </label>
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      Odesílám...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill mr-2"></i>
                      Odeslat zprávu
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Často kladené otázky
            </h2>
            <p className="text-xl text-gray-600">
              Odpovědi na nejčastější dotazy našich klientů
            </p>
          </div>

          <div className="space-y-2">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                  <i className="ri-time-line text-white text-sm"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Jak dlouho trvá implementace AI řešení?
                  </h3>
                  <p className="text-gray-600">
                    Doba implementace závisí na složitosti projektu. Jednoduché chatboty dokážeme spustit během 2–3 týdnů, 
                    komplexní systémy trvají obvykle 2–6 měsíců. Vždy začínáme pilotním projektem, který umožňuje vše otestovat v praxi.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-white text-sm"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Kolik stojí AI implementace?
                  </h3>
                  <p className="text-gray-600">
                    Cena se odvíjí od rozsahu a technické náročnosti řešení. Základní automatizace začíná na desítkách tisíc korun, 
                    komplexní systémy se pohybují ve stovkách tisíc. Nabízíme různé cenové modely včetně měsíčních plateb podle možností klienta.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                  <i className="ri-team-line text-white text-sm"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Potřebujeme vlastní IT tým?
                  </h3>
                  <p className="text-gray-600">
                    Ne, naše řešení jsou navržena tak, aby je zvládli běžní uživatelé bez speciálních znalostí. 
                    Zajišťujeme kompletní zaškolení a dlouhodobou technickou podporu.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                  <i className="ri-shield-check-line text-white text-sm"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Jak zajišťujete bezpečnost dat?
                  </h3>
                  <p className="text-gray-600">
                    Používáme nejmodernější šifrování a pravidelné bezpečnostní audity. Splňujeme požadavky GDPR a 
                    dbáme na ochranu citlivých informací ve všech fázích projektu.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                  <i className="ri-user-settings-line text-white text-sm"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Kdo musí být zapojen do implementace?
                  </h3>
                  <p className="text-gray-600">
                    Stačí základní spolupráce s vedením firmy a pracovníky, kteří budou systém využívat. 
                    Náš tým vás celým procesem provede.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                  <i className="ri-graduation-cap-line text-white text-sm"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Jak probíhá školení zaměstnanců?
                  </h3>
                  <p className="text-gray-600">
                    Po nasazení AI řešení organizujeme školení na míru pro váš tým, a to formou online nebo osobně. 
                    Vysvětlíme obsluhu technologií i možná rizika.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                  <i className="ri-rocket-line text-white text-sm"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Můžeme začít pilotním projektem?
                  </h3>
                  <p className="text-gray-600">
                    Ano, doporučujeme zahájit spolupráci testovacím projektem, na kterém si ověříte výhody AI pro vaše konkrétní potřeby.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                  <i className="ri-arrow-up-line text-white text-sm"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Jaké jsou hlavní výhody pro firmy?
                  </h3>
                  <p className="text-gray-600">
                    AI zrychluje procesy, šetří čas a náklady, zlepšuje zákaznickou zkušenost, minimalizuje chyby a 
                    umožňuje firmám efektivně růst.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                  <i className="ri-alert-line text-white text-sm"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Jaká jsou možná rizika?
                  </h3>
                  <p className="text-gray-600">
                    Nejasně definované cíle nebo nereálná očekávání mohou proces prodloužit. Důležitá je transparentnost, 
                    správné nastavení procesu a pravidelný monitoring výsledků.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}